import { Badge } from '@fluentui/react-components';

import { IdentitySuggestionUser } from '../../types/identity';

// Mapping status vocabulary (FD-098). Derivation rules come from the FD-103
// consumption notes — the chip only renders server-provided facts:
//   noManifest     device has null primaryUserSid (FD-102)
//   needsAttention suggestions row has needsAttention: true
//   mapped         existingMapping present
//   suggested      suggestions non-empty, no mapping
//   unmapped       empty suggestions, no mapping
//   pending        staged locally, not yet saved (client-side staging state)
export type MappingChipStatus =
  | 'noManifest'
  | 'unmapped'
  | 'suggested'
  | 'mapped'
  | 'needsAttention'
  | 'pending';

const LABELS: Record<MappingChipStatus, string> = {
  noManifest: 'No manifest',
  unmapped: 'Unmapped',
  suggested: 'Suggested',
  mapped: 'Mapped',
  needsAttention: 'Needs attention',
  pending: 'Pending save',
};

// Closed status palette (FLUENT_UI_ADDENDUM): sky/informative = in-progress
// ("Suggested"), green/success = done ("Mapped"), red/danger = needs operator
// action, zinc/subtle = neutral. `brand` (Rubix orange under rubixTheme) marks
// locally staged, unsaved edits.
const COLORS: Record<
  MappingChipStatus,
  'subtle' | 'informative' | 'success' | 'danger' | 'brand'
> = {
  noManifest: 'subtle',
  unmapped: 'subtle',
  suggested: 'informative',
  mapped: 'success',
  needsAttention: 'danger',
  pending: 'brand',
};

// Chip derivation per the FD-103 consumption notes, applied verbatim (in the
// notes' precedence order) — the frontend renders server facts, it does not
// compute match verdicts. Used by both the device grid and the mapping section.
export function deriveMappingChipStatus(row: IdentitySuggestionUser): MappingChipStatus {
  // FD-544/FD-545 — A MAPPING OUTRANKS `needsAttention`, and the order of these
  // first two lines is the whole decision.
  //
  // `needsAttention` describes how the SUGGESTION was derived — the server could
  // not resolve the source user, or the SID is in a format it does not parse. It
  // is NOT a statement about work remaining. Once a mapping exists and the
  // delivery gate accepts it, there is nothing for an operator to do, and the
  // server already agrees: `summary.mapped` counts the row as mapped. Testing
  // `needsAttention` first made the chip disagree with the tile beside it, and
  // reported outstanding work on a row whose device was already migrating
  // (observed live on staging, 2026-10-01).
  //
  // The `destinationReady === false` guard is NOT incidental tidying. Without it
  // a mapping the delivery gate will refuse (409 IDENTITY_NOT_READY) would read
  // as a green "Mapped" while holding every device behind it — and that inverse
  // defect was live too, whenever `needsAttention` happened to be false.
  //
  // The EXCEPTION REASON deliberately does not follow this precedence:
  // `deriveExceptionReason` still returns `sourceNotResolved` for a mapped row,
  // so the inspector keeps saying the source was never resolved. The chip answers
  // "is there work left"; the exception answers "what is wrong with this row". A
  // green Mapped chip beside an Exception block naming the unresolved source is
  // the honest pair, not a contradiction to reconcile.
  if (row.existingMapping?.destinationReady === false) return 'needsAttention';
  if (row.existingMapping) return 'mapped';
  if (row.needsAttention) return 'needsAttention';
  if (row.suggestions.length > 0) return 'suggested';
  return 'unmapped';
}

export default function MappingStatusChip({ status }: { status: MappingChipStatus }) {
  return (
    <Badge appearance="tint" color={COLORS[status]}>
      {LABELS[status]}
    </Badge>
  );
}
